<template>
	<section>
	<FormGroupBox title="Búsqueda de Precios">
		<!-- Basic Filters -->
		<div class="filter-section">
			<div class="section-header mb-3">
				<md-icon icon="tune" class="text-primary mr-2"></md-icon>
				<strong>Filtros Básicos</strong>
			</div>

			<b-form-row>
				<b-form-group class="col-12 col-md-6" label="Cotizar por*" label-class="font-weight-semibold">
					<div class="input-with-icon">
						<md-icon icon="business" class="input-icon text-muted"></md-icon>
						<b-form-select
							size="sm"
							v-model="relatedModelType"
							:options="[
								{ value: 'supplier', text: 'Supplier' }
							]"
							required
							class="pl-5"
						/>
					</div>
				</b-form-group>
				
				<b-form-group v-if="relatedModelType == 'supplier'" class="col-12 col-md-6" label="Proveedor*" label-class="font-weight-semibold">
					<div class="input-with-icon">
						<md-icon icon="local_shipping" class="input-icon-multiselect text-muted"></md-icon>
						<multiselect
							v-model="form.supplier"
							track-by="value"
							:options="suppliersOpts"
							label="text"
							placeholder="Seleccionar proveedor..."
							class="with-icon"
						/>
					</div>
				</b-form-group>
			</b-form-row>

			<b-form-row>
				<b-form-group label="Origen*" class="col-12 col-lg-4" label-class="font-weight-semibold">
					<vue-typeahead-bootstrap
						showAllResults
						size="sm"
						placeholder="Buscar origen..."
						v-model="pickupSearch"
						:serializer="(place) => place.name"
						:data="pickupData"
						@hit="(selected) => (form.pickup = selected)"
					>
						<template #suggestion="{ data, htmlText }">
							<span v-html="htmlText"></span><br /><small>{{
							data.address
							}}</small>
						</template>
						<template #prepend>
							<b-input-group-text
							><md-icon :icon="pickupTypeIcon"
							/></b-input-group-text>
						</template>
						<template #append>
							<b-button variant="primary" @click="pickupEdit = false">
							<b-spinner v-if="pickupLoading" small />
							<span v-else>X</span>
							</b-button>
						</template>
					</vue-typeahead-bootstrap>
				</b-form-group>
				<b-form-group label="Destino*" class="col-12 col-lg-4" label-class="font-weight-semibold">
					<vue-typeahead-bootstrap
						showAllResults
						size="sm"
						placeholder="Buscar destino..."
						v-model="dropoffSearch"
						:serializer="(place) => place.name"
						:data="dropoffData"
						@hit="(selected) => (form.dropoff = selected)"
					>
						<template #suggestion="{ data, htmlText }">
							<span v-html="htmlText"></span><br /><small>{{
							data.address
							}}</small>
						</template>
						<template #prepend>
							<b-input-group-text
							><md-icon :icon="dropoffTypeIcon"
							/></b-input-group-text>
						</template>
						<template #append>
							<b-button variant="primary" @click="dropoffEdit = false">
							<b-spinner v-if="dropoffLoading" small />
							<span v-else>X</span>
							</b-button>
						</template>
					</vue-typeahead-bootstrap>
				</b-form-group>
				<b-col class="col-12 col-lg-4">
					<b-form-group :label="fieldNameDate" label-class="font-weight-semibold">
						<div class="input-with-icon">
							<md-icon icon="event" class="input-icon text-muted"></md-icon>
							<b-form-datepicker
								aria-required="true"
								v-model="form.date"
								required
								size="sm"
								class="pl-5"
								locale="es"
								reset-button
								close-button
								:date-format-options="{
									year: 'numeric',
									month: 'short',
									day: '2-digit',
									weekday: 'short',
								}"
							/>
						</div>
					</b-form-group>
				</b-col>
			</b-form-row>
		</div>

		<!-- Advanced Filters Toggle -->
		<div class="advanced-toggle mb-3">
			<b-button
				variant="outline-secondary"
				size="sm"
				@click="form.advancedFields = !form.advancedFields"
				class="d-flex align-items-center"
			>
				<md-icon :icon="form.advancedFields ? 'expand_less' : 'expand_more'" class="mr-2"></md-icon>
				{{ form.advancedFields ? 'Ocultar' : 'Mostrar' }} campos avanzados
			</b-button>
		</div>

		<!-- Advanced Filters -->
		<b-collapse v-model="form.advancedFields">
			<div class="filter-section advanced-section">
				<div class="section-header mb-3">
					<md-icon icon="settings" class="text-primary mr-2"></md-icon>
					<strong>Filtros Avanzados</strong>
				</div>

				<b-form-row>
					<b-form-group class="col-12 col-md-6" label="ID de reservación:" label-class="font-weight-semibold">
						<div class="d-flex align-items-end gap-2">
							<div class="flex-grow-1">
								<b-form-input
									v-model="form.reservationId"
									size="sm"
									type="text"
									placeholder="Ej: 12345"
									:disabled="loadingReservation"
								>
								</b-form-input>
							</div>
							<b-button
								variant="info"
								size="sm"
								@click="loadReservationData"
								:disabled="!form.reservationId || loadingReservation"
								class="load-reservation-btn"
							>
								<b-spinner v-if="loadingReservation" small></b-spinner>
								<md-icon v-else icon="download"></md-icon>
								<span class="ml-1">Cargar</span>
							</b-button>
							<b-button
								variant="outline-secondary"
								size="sm"
								@click="clearReservationData"
								:disabled="loadingReservation"
								class="clear-reservation-btn"
								v-if="form.reservationId || loadedReservationServiceType"
							>
								<md-icon icon="clear"></md-icon>
								<span class="ml-1">Limpiar</span>
							</b-button>
						</div>
						<small class="text-muted d-block mt-1">Ingresa el ID y haz clic en "Cargar" para llenar el formulario automáticamente</small>
					</b-form-group>
				</b-form-row>

				<b-form-row>
					<b-form-group class="col-12 col-md-4" label="Vender por:" label-class="font-weight-semibold">
						<div class="input-with-icon">
							<md-icon icon="store" class="input-icon-multiselect text-muted"></md-icon>
							<multiselect
								v-model="form.sellSalesChannel"
								track-by="value"
								:options="channelOpts"
								label="text"
								placeholder="Seleccionar canal..."
								class="with-icon"
							/>
						</div>
					</b-form-group>

					<b-col md="3" v-if="relatedModelType=='supplier'">
						<b-form-group label="Fecha de Reserva" label-class="font-weight-semibold">
							<div class="input-with-icon">
								<md-icon icon="date_range" class="input-icon text-muted"></md-icon>
								<b-form-datepicker
									aria-required="true"
									v-model="form.bookingDate"
									required
									size="sm"
									class="pl-5"
									locale="es"
									reset-button
									close-button
									:date-format-options="{
										year: 'numeric',
										month: 'short',
										day: '2-digit',
										weekday: 'short',
									}"
								/>
							</div>
						</b-form-group>
					</b-col>

					<b-col md="4">
						<b-form-group label="Viaje Redondo" label-class="font-weight-semibold">
							<div class="roundtrip-switch">
								<b-form-checkbox
									v-model="form.roundtrip"
									name="check-button"
									switch
									size="lg"
								>
									<span class="ml-2">{{ form.roundtrip ? 'Sí' : 'No' }}</span>
								</b-form-checkbox>
							</div>
						</b-form-group>
					</b-col>
				</b-form-row>

				<b-form-row class="mt-2">
					<b-col md="6">
						<b-form-group label="Llegada" label-class="font-weight-semibold">
							<div class="date-time-group">
								<div class="input-with-icon mb-2">
									<md-icon icon="event" class="input-icon text-muted"></md-icon>
									<b-form-datepicker
										aria-required="true"
										v-model="form.serviceDate"
										required
										size="sm"
										class="pl-5"
										locale="es"
										reset-button
										close-button
										:date-format-options="{
											year: 'numeric',
											month: 'short',
											day: '2-digit',
											weekday: 'short',
										}"
									/>
								</div>
								<div class="input-with-icon">
									<md-icon icon="schedule" class="input-icon text-muted"></md-icon>
									<vue-timepicker
										v-model="form.serviceTime"
										manual-input
										class="pl-5"
									></vue-timepicker>
								</div>
							</div>
						</b-form-group>
					</b-col>
					<b-col md="6">
						<b-form-group label="Salida" :disabled="!form.roundtrip" label-class="font-weight-semibold">
							<div class="date-time-group">
								<div class="input-with-icon mb-2">
									<md-icon icon="event" class="input-icon text-muted"></md-icon>
									<b-form-datepicker
										aria-required="true"
										v-model="form.returnServiceDate"
										locale="es"
										reset-button
										close-button
										size="sm"
										class="pl-5"
										:disabled="!form.roundtrip"
										:date-format-options="{
											year: 'numeric',
											month: 'short',
											day: '2-digit',
											weekday: 'short',
										}"
									/>
								</div>
								<div class="input-with-icon">
									<md-icon icon="schedule" class="input-icon text-muted"></md-icon>
									<vue-timepicker
										v-model="form.returnServiceTime"
										manual-input
										:disabled="!form.roundtrip"
										class="pl-5"
									></vue-timepicker>
								</div>
							</div>
						</b-form-group>
					</b-col>
				</b-form-row>
			</div>
		</b-collapse>

		<!-- Search Button -->
		<div class="text-center mt-4">
			<b-button @click="doQuote" variant="primary" size="lg" class="search-button">
				<md-icon icon="search" class="mr-2"></md-icon>
				Buscar Precios
			</b-button>
		</div>

		<!-- Reservation Service Type Banner -->
		<b-alert v-if="loadedReservationServiceType" show variant="info" class="mt-4 mb-0">
			<div class="d-flex align-items-center">
				<md-icon icon="directions_car" class="mr-2"></md-icon>
				<div>
					<strong>Tipo de servicio de la reservación:</strong>
					<span class="ml-2">{{ loadedReservationServiceType.name }}</span>
					<small class="text-muted ml-2">(ID: {{ loadedReservationServiceType.id }})</small>
				</div>
			</div>
		</b-alert>
	</FormGroupBox>
	<FormGroupBox title="Prices" v-if="!loading">
		<b-tabs content-class="mt-3" fill>
			<!-- Tab de Llegadas (Ida) -->
			<b-tab active>
				<template #title>
					<md-icon icon="arrow_forward" class="mr-1"></md-icon>
					Llegadas ({{ arrivalRatePlans.length }})
				</template>

				<div v-if="arrivalRatePlans.length === 0" class="text-center text-muted py-5">
					<md-icon icon="info" class="large-icon mb-2"></md-icon>
					<p>No se encontraron precios para llegadas</p>
				</div>

				<div v-for="(price, index) in arrivalRatePlans" :key="'arrival-' + index" class="rate-plan-card mb-4">
					<!-- Aviso si el plan no tiene el tipo de servicio de la reservación -->
					<b-alert
						v-if="loadedReservationServiceType && !ratePlanHasReservationServiceType(price)"
						show
						variant="warning"
						class="mb-0 rounded-0 border-left-0 border-right-0 border-top-0"
					>
						<div class="d-flex align-items-center">
							<md-icon icon="warning" class="mr-2"></md-icon>
							<span>
								Este plan tarifario <strong>no tiene</strong> el tipo de servicio
								<strong>"{{ loadedReservationServiceType.name }}"</strong> de la reservación.
							</span>
						</div>
					</b-alert>
					<div class="rate-plan-header">
						<div class="d-flex align-items-start justify-content-between flex-wrap">
							<div class="flex-grow-1">
								<h5 class="mb-1">
									<b class="text-primary">[{{price.ratePlan.supplier.id}}] {{price.ratePlan.supplier.name}}</b>
								</h5>
								<h6 class="mb-2">
									<md-icon icon="loyalty" class="small-icon text-muted mr-1"></md-icon>
									{{ price.ratePlan.name }}
								</h6>
								<div class="text-muted mb-2">
									<md-icon icon="place" class="small-icon mr-1"></md-icon>
									{{price.pickupZone.name}}
									<md-icon icon="arrow_forward" class="small-icon mx-1"></md-icon>
									{{price.dropoffZone.name}}
								</div>
							</div>
							<div class="service-type-badge ml-3">
								<b-badge :variant="getServiceVariant(price.service)" class="py-2 px-3">
									<md-icon :icon="getServiceIcon(price.service)" class="mr-1"></md-icon>
									{{ getServiceLabel(price.service) }}
								</b-badge>
							</div>
						</div>

						<!-- Travel Window and Service Time -->
						<div class="rate-plan-details mt-2">
							<b-row>
								<b-col cols="12" md="6" v-if="price.ratePlan.startDate">
									<div class="detail-item mb-2">
										<small class="text-muted">
											<md-icon icon="event" class="small-icon mr-1"></md-icon>
											Vigencia:
										</small>
										<div class="detail-value">
											{{ formatDate(price.ratePlan.startDate) }}
											-
											{{ formatDate(price.ratePlan.endDate) }}
										</div>
									</div>
								</b-col>
								<b-col cols="12" md="6" v-if="price.ratePlan.travelWindowStartDate && price.ratePlan.travelWindowEndDate">
									<div class="detail-item mb-2">
										<small class="text-muted">
											<md-icon icon="date_range" class="small-icon mr-1"></md-icon>
											Fecha de viaje:
										</small>
										<div class="detail-value">
											{{ formatDate(price.ratePlan.travelWindowStartDate) }}
											-
											{{ formatDate(price.ratePlan.travelWindowEndDate) }}
										</div>
									</div>
								</b-col>
							</b-row>
							<b-row v-if="price.ratePlan.serviceStartTime && price.ratePlan.serviceEndTime">
								<b-col cols="12" md="6">
									<div class="detail-item">
										<small class="text-muted">
											<md-icon icon="schedule" class="small-icon mr-1"></md-icon>
											Horario Servicio:
										</small>
										<div class="detail-value">
											{{ formatTime(price.ratePlan.serviceStartTime) }} - {{ formatTime(price.ratePlan.serviceEndTime) }}
										</div>
									</div>
								</b-col>
							</b-row>
						</div>
					</div>

					<b-table striped hover :items="price.prices" :fields="fields" class="mb-0" stacked="lg" small>
						<template #cell(oneway)="row">
							<span>${{row.item.oneway}}</span>
						</template>
						<template #cell(roundtrip)="row">
							<span>${{row.item.roundtrip}}</span>
						</template>
						<template #cell(currency)="">
							<span class="text-uppercase">{{price.ratePlan.currency}}</span>
						</template>
						<template #cell(priceAfterPromotion)="row">
							<span v-if="!row.item.promo.found">--</span>
							<span v-else>
								<b v-if="lastSearched.roundtrip">[Roundtrip]</b>
								<b v-else>[Oneway]</b>
								${{row.item.promo.price}} {{price.ratePlan.currency}}
								<b-button variant="outline-success"  @click="showTracePromotion(row.item.promo.trace)" class="__shake" size="sm">
									<md-icon icon="local_offer" type="button" />
								</b-button>
							</span>
						</template>
					</b-table>
				</div>
			</b-tab>

			<!-- Tab de Salidas (Regreso) -->
			<b-tab>
				<template #title>
					<md-icon icon="arrow_back" class="mr-1"></md-icon>
					Salidas ({{ departureRatePlans.length }})
				</template>

				<div v-if="departureRatePlans.length === 0" class="text-center text-muted py-5">
					<md-icon icon="info" class="large-icon mb-2"></md-icon>
					<p>No se encontraron precios para salidas</p>
				</div>

				<div v-for="(price, index) in departureRatePlans" :key="'departure-' + index" class="rate-plan-card mb-4">
					<!-- Aviso si el plan no tiene el tipo de servicio de la reservación -->
					<b-alert
						v-if="loadedReservationServiceType && !ratePlanHasReservationServiceType(price)"
						show
						variant="warning"
						class="mb-0 rounded-0 border-left-0 border-right-0 border-top-0"
					>
						<div class="d-flex align-items-center">
							<md-icon icon="warning" class="mr-2"></md-icon>
							<span>
								Este plan tarifario <strong>no tiene</strong> el tipo de servicio
								<strong>"{{ loadedReservationServiceType.name }}"</strong> de la reservación.
							</span>
						</div>
					</b-alert>
					<div class="rate-plan-header">
						<div class="d-flex align-items-start justify-content-between flex-wrap">
							<div class="flex-grow-1">
								<h5 class="mb-1">
									<b class="text-primary">[{{price.ratePlan.supplier.id}}] {{price.ratePlan.supplier.name}}</b>
								</h5>
								<h6 class="mb-2">
									<md-icon icon="loyalty" class="small-icon text-muted mr-1"></md-icon>
									{{ price.ratePlan.name }}
								</h6>
								<div class="text-muted mb-2">
									<md-icon icon="place" class="small-icon mr-1"></md-icon>
									{{price.pickupZone.name}}
									<md-icon icon="arrow_forward" class="small-icon mx-1"></md-icon>
									{{price.dropoffZone.name}}
								</div>
							</div>
							<div class="service-type-badge ml-3">
								<b-badge :variant="getServiceVariant(price.service)" class="py-2 px-3">
									<md-icon :icon="getServiceIcon(price.service)" class="mr-1"></md-icon>
									{{ getServiceLabel(price.service) }}
								</b-badge>
							</div>
						</div>

						<!-- Travel Window and Service Time -->
						<div class="rate-plan-details mt-2">
							<b-row>
								<b-col cols="12" md="6" v-if="price.ratePlan.startDate">
									<div class="detail-item mb-2">
										<small class="text-muted">
											<md-icon icon="event" class="small-icon mr-1"></md-icon>
											Vigencia:
										</small>
										<div class="detail-value">
											{{ formatDate(price.ratePlan.startDate) }}
											-
											{{ formatDate(price.ratePlan.endDate) }}
										</div>
									</div>
								</b-col>
								<b-col cols="12" md="6" v-if="price.ratePlan.travelWindowStartDate && price.ratePlan.travelWindowEndDate">
									<div class="detail-item mb-2">
										<small class="text-muted">
											<md-icon icon="date_range" class="small-icon mr-1"></md-icon>
											Fecha de viaje:
										</small>
										<div class="detail-value">
											{{ formatDate(price.ratePlan.travelWindowStartDate) }}
											-
											{{ formatDate(price.ratePlan.travelWindowEndDate) }}
										</div>
									</div>
								</b-col>
							</b-row>
							<b-row v-if="price.ratePlan.serviceStartTime && price.ratePlan.serviceEndTime">
								<b-col cols="12" md="6">
									<div class="detail-item">
										<small class="text-muted">
											<md-icon icon="schedule" class="small-icon mr-1"></md-icon>
											Horario Servicio:
										</small>
										<div class="detail-value">
											{{ formatTime(price.ratePlan.serviceStartTime) }} - {{ formatTime(price.ratePlan.serviceEndTime) }}
										</div>
									</div>
								</b-col>
							</b-row>
						</div>
					</div>

					<b-table striped hover :items="price.prices" :fields="fields" class="mb-0" stacked="lg" small>
						<template #cell(oneway)="row">
							<span>${{row.item.oneway}}</span>
						</template>
						<template #cell(roundtrip)="row">
							<span>${{row.item.roundtrip}}</span>
						</template>
						<template #cell(currency)="">
							<span class="text-uppercase">{{price.ratePlan.currency}}</span>
						</template>
						<template #cell(priceAfterPromotion)="row">
							<span v-if="!row.item.promo.found">--</span>
							<span v-else>
								<b v-if="lastSearched.roundtrip">[Roundtrip]</b>
								<b v-else>[Oneway]</b>
								${{row.item.promo.price}} {{price.ratePlan.currency}}
								<b-button variant="outline-success"  @click="showTracePromotion(row.item.promo.trace)" class="__shake" size="sm">
									<md-icon icon="local_offer" type="button" />
								</b-button>
							</span>
						</template>
					</b-table>
				</div>
			</b-tab>
		</b-tabs>
		<!-- <b-row v-for="(price,index) in prices" :key="index">
				<b-col>{{price.serviceType.name}}</b-col>
				<b-col>
					<p>One Way: ${{price.oneway}} {{currency}}</p>
				</b-col>
				<b-col>
					<b-input-group size="sm">
						<b-input-group-prepend is-text>Roundtrip</b-input-group-prepend>
						<b-input-group-prepend is-text>$</b-input-group-prepend>
						<b-form-input disabled type="number" :value="price.roundtrip" required  />
						<b-input-group-append is-text>{{currency}}</b-input-group-append>
					</b-input-group>
				</b-col>
			</b-row> -->
	</FormGroupBox>
	<b-overlay :show="loading" no-wrap rounded></b-overlay>
	<b-modal ref="modalPromotionTrace" size="lg">
		<TracePromotion :tracePromotion="tracePromotion"></TracePromotion>
	</b-modal>
	</section>
</template>

<script>
import FormGroupBox from "../../helpers/FormGroupBox";
import VueTypeaheadBootstrap from "vue-typeahead-bootstrap";
import { debounce } from "debounce";
import moment from 'moment'
import Multiselect from 'vue-multiselect';
import TracePromotion from "../../helpers/TracePromo";
import vueTimepicker from "vue2-timepicker";
export default {
  components: { FormGroupBox, VueTypeaheadBootstrap,Multiselect,TracePromotion,vueTimepicker },
  data() {
	const today = moment().format("YYYY-MM-DD");
	let pickupPlaceModel  = { id: null, name: "", isAirport: false };
	let dropoffPlaceModel = { id: null, name: "", isAirport: false };

    return {
		relatedModelType: "supplier",
		validSuppliers: [],
		ratePlans: [],
		loading: false,
		loadingReservation: false,
		pickupSearch: "",
		dropoffSearch: "",
		pickupLoading: false,
		dropoffLoading: false,
		pickupData: [],
		dropoffData: [],
		lastSearched:null,
		form:{
			advancedFields:false,
			date:today,
			supplier:null,
			roundtrip:false,
			serviceDate:today,
			serviceTime:"",
			returnServiceDate:null,
			returnServiceTime:"",
			bookingDate:today,
			pickup: {...pickupPlaceModel},
			dropoff: {...dropoffPlaceModel},
			sellSalesChannel:null,
			reservationId: null
		},
		tracePromotion:{},
		loadedReservationServiceType: null, // Tipo de servicio de la reservación cargada
    };
  },
  methods: {
	showTracePromotion(trace) {
		this.tracePromotion = trace
		this.$refs["modalPromotionTrace"].show();
    },
	getServiceLabel(service) {
		return service === 1 ? 'LLegada' : service === 2 ? 'Regreso' : 'Servicio';
	},
	getServiceIcon(service) {
		return service === 1 ? 'arrow_forward' : service === 2 ? 'arrow_back' : 'swap_horiz';
	},
	getServiceVariant(service) {
		return service === 1 ? 'info' : service === 2 ? 'success' : 'secondary';
	},
	ratePlanHasReservationServiceType(ratePlan) {
		// Si no hay tipo de servicio de reservación cargado, no mostrar aviso
		if (!this.loadedReservationServiceType) return true;
		// Verificar si algún precio del plan tiene el mismo serviceType.id
		return ratePlan.prices.some(price =>
			price.serviceType && price.serviceType.id === this.loadedReservationServiceType.id
		);
	},
	clearReservationData() {
		this.form.reservationId = null;
		this.loadedReservationServiceType = null;
	},
	formatDate(dateString) {
		if (!dateString) return '';
		if (dateString == 'Not set') return '';
		try {
			const date = new Date(dateString);
			const options = { year: 'numeric', month: 'short', day: '2-digit' };
			return date.toLocaleDateString('es-ES', options);
		} catch (error) {
			return dateString;
		}
	},
	formatTime(timeString) {
		if (!timeString) return '';
		try {
			const timeParts = timeString.split(':');
			if (timeParts.length >= 2) {
				return `${timeParts[0]}:${timeParts[1]}`;
			}
			return timeString;
		} catch (error) {
			return timeString;
		}
	},
    searchPlace(attrName, term) {
      if (term != "") {
        this[`${attrName}Loading`] = true;
        let query = {
          term,
          hasLocationData: true,
        };
        this.$root.api
          .post(`/places/search`, query)
          .then((res) => {
            this[`${attrName}Data`] = res.data;
            this[`${attrName}Loading`] = false;
          })
          .catch((err) => {
            console.log(err);
          });
      }
    },
	validate(){
		try{
			if(this.$root.isEmpty(this.form.pickup.id)) throw "Pickup is required";
			if(this.$root.isEmpty(this.form.dropoff.id)) throw "Dropoff is required";
			if(this.$root.isEmpty(this.form.date)) throw "Date is required";
			if(this.form.advancedFields){
				if(this.relatedModelType=="supplier"){
					if(this.$root.isEmpty(this.form.bookingDate)) throw "Booking Date is required";
				}
				if(this.$root.isEmpty(this.form.serviceDate)) throw "Service Date is required";
				if( this.form.roundtrip && this.$root.isEmpty(this.form.returnServiceDate)) throw "return service Date is required";
			}
		}catch(err){
			this.$root.showAlert(err,{ variant: "warning" })
			return false;
		}
		return true;
	},
    doQuote() {
		if(!this.validate()) return;
		let query = {
			...this.form,
			supplier: this.form.supplier?.value ?? null,
			modelType: this.relatedModelType
		};
		this.lastSearched = {...query};
		this.loading = true;
		this.$root.api
		.post("/rates/quote", query)
		.then((res) => {
			this.ratePlans = res.data
		})
		.catch(this.$root.handleRequestError)
		.then(() => {
			this.loading = false;
		});
    },
	async loadReservationData() {
		if (!this.form.reservationId) {
			this.$root.showAlert("Por favor ingresa un ID de reservación", { variant: "warning" });
			return;
		}

		this.loadingReservation = true;

		try {
			// Hacer la petición GET al API con el ID de reservación
			const res = await this.$root.api.get(`/reservations/${this.form.reservationId}`);
			const reservation = res.data;

			// Verificar que tenga servicios de transporte
			if (!reservation.transportServices || reservation.transportServices.length === 0) {
				throw new Error("La reservación no tiene servicios de transporte");
			}

			// Obtener el primer servicio (ida)
			const firstService = reservation.transportServices[0];

			// Guardar el tipo de servicio de la reservación
			if (firstService.serviceType) {
				this.loadedReservationServiceType = {
					id: firstService.serviceType.id,
					name: firstService.serviceType.name,
					code: firstService.serviceType.code
				};
			} else {
				this.loadedReservationServiceType = null;
			}

			// Buscar si hay un segundo servicio (regreso)
			const hasReturn = reservation.transportServices.length > 1;
			const returnService = hasReturn ? reservation.transportServices[1] : null;

			// Cargar proveedor del primer servicio
			if (firstService.supplier) {
				const supplier = this.validSuppliers.find(s => s.id === firstService.supplier.id);
				if (supplier) {
					this.form.supplier = { value: supplier, text: supplier.name };
				}
			}

			// Cargar origen
			if (firstService.pickup) {
				this.form.pickup = {
					id: firstService.pickup.id,
					name: firstService.pickup.name,
					address: firstService.pickup.address || '',
					ref: firstService.pickup.ref,
					isAirport: firstService.pickup.isAirport || false,
					type: firstService.pickup.type || 'scplace'
				};
				this.pickupSearch = firstService.pickup.name;
			}

			// Cargar destino
			if (firstService.dropoff) {
				this.form.dropoff = {
					id: firstService.dropoff.id,
					name: firstService.dropoff.name,
					address: firstService.dropoff.address || '',
					ref: firstService.dropoff.ref,
					isAirport: firstService.dropoff.isAirport || false,
					type: firstService.dropoff.type || 'scplace'
				};
				this.dropoffSearch = firstService.dropoff.name;
			}

			// Cargar fecha de reservación como bookingDate
			if (reservation.reservationDatetime) {
				this.form.bookingDate = moment(reservation.reservationDatetime).format("YYYY-MM-DD");
			}

			// Cargar fecha y hora del servicio de ida desde pickupDatetime
			if (firstService.pickupDatetime) {
				const pickupMoment = moment(firstService.pickupDatetime);
				this.form.serviceDate = pickupMoment.format("YYYY-MM-DD");
				this.form.serviceTime = pickupMoment.format("HH:mm");
				// También establecer la fecha general
				this.form.date = pickupMoment.format("YYYY-MM-DD");
			}

			// Cargar información de viaje redondo si existe
			if (hasReturn && returnService) {
				this.form.roundtrip = true;

				if (returnService.pickupDatetime) {
					const returnMoment = moment(returnService.pickupDatetime);
					this.form.returnServiceDate = returnMoment.format("YYYY-MM-DD");
					this.form.returnServiceTime = returnMoment.format("HH:mm");
				}
			} else {
				this.form.roundtrip = false;
			}

			// Cargar canal de venta
			if (firstService.salesChannel) {
				const channel = this.channelOpts.find(c =>
					c.value === firstService.salesChannel.id ||
					c.value === firstService.salesChannelId
				);
				if (channel) {
					this.form.sellSalesChannel = channel;
				}
			}

			// Expandir campos avanzados automáticamente si se cargaron datos
			this.form.advancedFields = true;

			this.$root.showAlert("Datos de la reservación cargados exitosamente", { variant: "success" });

		} catch (error) {
			console.error("Error al cargar la reservación:", error);
			this.$root.showAlert(
				error.response?.data?.message || error.message || "No se pudo cargar la información de la reservación. Verifica que el ID sea correcto.",
				{ variant: "danger" }
			);
		} finally {
			this.loadingReservation = false;
		}
	},
  },
  computed: {
	fieldNameDate(){
		if(this.relatedModelType=="supplier")return "Fecha de asignación"
		return "Booking date"
	},
    pickupTypeIcon() {
      let icon = "location_on";
      if (this.form.pickup.isAirport) icon = "flight";
      return icon;
    },
    suppliersOpts() {
		let opts = [{value:null,text:"All"}]
		let dbOptions = this.validSuppliers.map((sup) => ({ value: sup, text: sup.name }))
		let all  = [...opts,...dbOptions]
		return all;
    },
    dropoffTypeIcon() {
      let icon = "location_on";
      if (this.form.dropoff.isAirport) icon = "flight";
      return icon;
    },
	fields(){
		let columns= [
			{ key:"serviceType.name", label: "Service type" },
			{ key:"serviceType.maxPax", label: "Max pax" },
			{ key:"oneway", label: "Oneway" },
			{ key:"roundtrip", label: "roundtrip" },
			{ key:"currency", label: "currency" }
		]
		if(this.lastSearched.advancedFields){
			columns.push({ key:"priceAfterPromotion", label: "Price after Promotion" })
		}
		return columns;
	},
	channelOpts(){
		let aux= [...this.$root.auxVars.salesChannelsOpts];
		return aux;
	},
	arrivalRatePlans() {
		// Filtrar planes con service = 1 (Ida/Llegadas)
		return this.ratePlans.filter(plan => plan.service === 1);
	},
	departureRatePlans() {
		// Filtrar planes con service = 2 (Regreso/Salidas)
		return this.ratePlans.filter(plan => plan.service === 2);
	},
  },
  mounted() {
	this.$root.api
		.get("/rates/valid-suppliers")
		.then((res) => {
		this.validSuppliers = res.data;
		})
		.catch(this.$root.handleRequestError);
	this.$root.auxVars.loadResourcesOpts({vm: this, resourceNames: ['salesChannelsOpts']})

	if(process.env.NODE_ENV !== "production"){//test
		setTimeout(()=>{
			this.form.pickup = {
				id: 51543,
				name: "Cancun, Quintana Roo, Mexico",
				address: "Cancun - Chetumal Km 22, 77565 Canc\u00fan, Q.R., Mexico",
				ref: "scplace-51543",
				isAirport: true,
				type:"scplace"
			};
			this.form.dropoff = {
				id: 10488,
				name: "Adhara Hacienda Cancun",
				address: "Adhara Hacienda Cancun, Av Carlos Nader 6, 2A, 77500 Canc\u00fan, Q.R., Mexico",
				ref: "scplace-10488",
				isAirport: false,
				type:"scplace"
			}
			this.pickupSearch= this.form.pickup.name
			this.dropoffSearch= this.form.dropoff.name
		},0)
	}
  },
  watch: {
    pickupSearch: debounce(function (term) {
      this.searchPlace("pickup", term);
    }, 500),
    dropoffSearch: debounce(function (term) {
      this.searchPlace("dropoff", term);
    }, 500),
    // pickup(){
    // 	this.doQuote()
    // },
    // dropoff(){
    // 	this.doQuote()
    // },
    // supplier(){
    // 	this.doQuote()
    // }
  },
};
</script>

<style scoped>
/* Filter Section */
.filter-section {
	background-color: #f8f9fa;
	padding: 1.25rem;
	border-radius: 0.5rem;
	border: 1px solid #e9ecef;
	margin-bottom: 1.5rem;
}

.advanced-section {
	background-color: #ffffff;
	border: 1px solid #dee2e6;
}

.section-header {
	display: flex;
	align-items: center;
	font-size: 1rem;
	color: #212529;
}

.section-header md-icon {
	font-size: 20px;
}

/* Input with Icon */
.input-with-icon {
	position: relative;
}

.input-icon {
	position: absolute;
	left: 0.75rem;
	top: 50%;
	transform: translateY(-50%);
	font-size: 18px;
	z-index: 5;
	pointer-events: none;
}

.input-icon-multiselect {
	position: absolute;
	left: 0.75rem;
	top: 0.75rem;
	font-size: 18px;
	z-index: 5;
	pointer-events: none;
}

.input-with-icon .form-control.pl-5,
.input-with-icon .b-form-datepicker.pl-5 {
	padding-left: 2.5rem !important;
}

.input-with-icon .vue__time-picker.pl-5 {
	padding-left: 2.5rem;
}

.multiselect.with-icon >>> .multiselect__tags {
	padding-left: 2.5rem;
}

/* Advanced Toggle */
.advanced-toggle {
	display: flex;
	justify-content: center;
}

.advanced-toggle .btn {
	border-radius: 1.5rem;
	font-weight: 500;
	padding: 0.5rem 1.25rem;
	transition: all 0.3s ease;
}

.advanced-toggle .btn:hover {
	transform: translateY(-1px);
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Roundtrip Switch */
.roundtrip-switch {
	padding: 0.5rem 0;
}

.roundtrip-switch .custom-switch {
	display: flex;
	align-items: center;
}

/* Date Time Group */
.date-time-group {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

/* Search Button */
.search-button {
	padding: 0.75rem 2.5rem;
	font-weight: 600;
	border-radius: 2rem;
	transition: all 0.3s ease;
	display: inline-flex;
	align-items: center;
	box-shadow: 0 2px 8px rgba(13, 110, 253, 0.25);
}

.search-button:hover {
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(13, 110, 253, 0.35);
}

.search-button md-icon {
	font-size: 20px;
}

/* Rate Plan Cards */
.rate-plan-card {
	background: #fff;
	border: 1px solid #dee2e6;
	border-radius: 0.375rem;
	overflow: hidden;
}

.rate-plan-header {
	padding: 1rem 1.25rem;
	background-color: #f8f9fa;
	border-bottom: 2px solid #dee2e6;
}

.rate-plan-header h5 {
	font-size: 1.1rem;
	font-weight: 600;
	color: #212529;
}

.rate-plan-header h6 {
	font-size: 0.95rem;
	font-weight: 500;
	color: #495057;
}

.small-icon {
	font-size: 16px;
	vertical-align: middle;
}

.service-type-badge {
	display: flex;
	align-items: center;
}

.service-type-badge .badge {
	font-size: 0.9rem;
	font-weight: 600;
	display: flex;
	align-items: center;
}

.rate-plan-details {
	padding-top: 0.75rem;
	border-top: 1px solid #e9ecef;
	margin-top: 0.75rem;
}

.detail-item {
	margin-bottom: 0;
}

.detail-item small {
	display: block;
	color: #6c757d;
	font-size: 0.75rem;
	margin-bottom: 0.25rem;
	font-weight: 500;
}

.detail-value {
	font-size: 0.9rem;
	color: #495057;
	font-weight: 500;
}

/* Load Reservation Button */
.load-reservation-btn {
	display: inline-flex;
	align-items: center;
	white-space: nowrap;
	font-weight: 500;
	transition: all 0.2s ease;
}

.load-reservation-btn:hover:not(:disabled) {
	transform: translateY(-1px);
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.load-reservation-btn md-icon {
	font-size: 18px;
}

/* Clear Reservation Button */
.clear-reservation-btn {
	display: inline-flex;
	align-items: center;
	white-space: nowrap;
	font-weight: 500;
	transition: all 0.2s ease;
}

.clear-reservation-btn:hover:not(:disabled) {
	transform: translateY(-1px);
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.clear-reservation-btn md-icon {
	font-size: 18px;
}

/* Gap utility for flexbox */
.gap-2 {
	gap: 0.5rem;
}

/* Responsive */
@media (max-width: 768px) {
	.filter-section {
		padding: 1rem;
	}

	.section-header {
		font-size: 0.9rem;
	}

	.search-button {
		padding: 0.65rem 2rem;
		font-size: 0.9rem;
	}

	.rate-plan-header {
		padding: 0.75rem 1rem;
	}

	.service-type-badge {
		margin-left: 0 !important;
		margin-top: 0.5rem;
		width: 100%;
	}

	.service-type-badge .badge {
		width: 100%;
		justify-content: center;
	}

	.date-time-group {
		margin-bottom: 1rem;
	}

	.load-reservation-btn span,
	.clear-reservation-btn span {
		display: none;
	}

	.load-reservation-btn,
	.clear-reservation-btn {
		padding: 0.375rem 0.5rem;
	}
}

/* Label Styling */
.font-weight-semibold {
	font-weight: 600;
	color: #495057;
	font-size: 0.875rem;
}

/* Tab Empty State */
.large-icon {
	font-size: 48px;
	opacity: 0.5;
}

/* Tabs Styling */
.nav-tabs .nav-link {
	font-weight: 500;
	color: #495057;
}

.nav-tabs .nav-link.active {
	font-weight: 600;
	color: #0d6efd;
}

.nav-tabs .nav-link md-icon {
	vertical-align: middle;
}
</style>
